import { useCallback, useMemo, useState } from 'react';

export interface CompactDrillView {
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly label: string;
}
export interface CompactDrill {
  readonly stack: readonly CompactDrillView[];
  readonly active: CompactDrillView | null;
  readonly push: (view: CompactDrillView) => void;
  readonly pop: () => void;
  readonly clear: () => void;
}

// Local (compact-only) drill stack. Deliberately NOT the global vqeStore
// drillStack, which targets the full workspace renderer.
export function useCompactDrill(): CompactDrill {
  const [stack, setStack] = useState<readonly CompactDrillView[]>([]);
  const push = useCallback((view: CompactDrillView) => setStack((s) => [...s, view]), []);
  // Idempotent: return the same state reference when nothing changes so React
  // bails out and does not trigger a re-render (prevents the infinite-loop in
  // CompactVqePopover's `useEffect([open, drill])` when the popover is closed).
  const pop = useCallback(() => setStack((s) => (s.length === 0 ? s : s.slice(0, -1))), []);
  const clear = useCallback(() => setStack((s) => (s.length === 0 ? s : [])), []);
  // Stabilize the returned object so consumers (e.g. useEffect deps) do not
  // see a new reference on every render.
  return useMemo(
    () => ({ stack, active: stack.length > 0 ? stack[stack.length - 1]! : null, push, pop, clear }),
    [stack, push, pop, clear],
  );
}
